/**
 * Generación y verificación de ítems de práctica matemática.
 *
 * Todo es puro y determinista: con la misma semilla se obtienen los mismos
 * ítems, así que un minijuego nunca depende del azar para ser correcto y
 * puede probarse sin DOM.
 *
 * Cada ítem incluye un **distractor de misconcepción**: la respuesta que da
 * un alumno que aplica el procedimiento erróneo típico de ese contenido
 * (sumar sin llevar, restar sin prestar, multiplicar sólo el numerador...).
 * Elegirlo permite explicar justo ese error en lugar de un "está mal" genérico.
 */

import type { ItemGenerator } from '../types/game';

const formatter = new Intl.NumberFormat('es-MX');

export function formatNumber(value: number): string {
  return formatter.format(value);
}

/** Ítem de práctica listo para pintarse en cualquier arquetipo numérico. */
export interface GeneratedItem {
  /** Contenido del que proviene, para elegir la retroalimentación. */
  tipo: ItemGenerator['tipo'];
  /** Pregunta completa. */
  pregunta: string;
  /** Expresión que se muestra destacada (p. ej. "1 250 + 985"). */
  expresion: string;
  /** Cuatro opciones ordenadas de forma determinista. */
  opciones: string[];
  /** Índice de la opción correcta dentro de `opciones`. */
  correcta: number;
  /** Respuesta canónica: número en aritmética, texto en fracciones. */
  respuesta: number | string;
  /** Índice del distractor de misconcepción, o null si no aplica. */
  distractorIndex: number | null;
  /** Explicación del error típico, para mostrar cuando se elige el distractor. */
  misconception: string | null;
  /** Por qué la respuesta correcta lo es. */
  explicacion: string;
}

/** Generador pseudoaleatorio con semilla (mulberry32). */
export function createRng(seed: number): () => number {
  let state = (seed >>> 0) || 0x9e3779b9;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function intInRange(rng: () => number, min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  if (hi < lo) return lo;
  return lo + Math.floor(rng() * (hi - lo + 1));
}

function shuffle<T>(rng: () => number, items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Construye las opciones: la correcta más distractores distintos, barajadas.
 * `padding` aporta relleno cuando los distractores se repiten entre sí.
 */
function buildOptions(
  rng: () => number,
  correct: string,
  distractors: string[],
  padding: (index: number) => string,
  size = 4
): { opciones: string[]; correcta: number; distractorIndex: number | null } {
  const pool: string[] = [];
  let misconceptionIndex: number | null = null;
  const seen = new Set<string>([correct]);

  for (const candidate of distractors) {
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    if (misconceptionIndex === null) misconceptionIndex = pool.length;
    pool.push(candidate);
  }
  for (let i = 0; pool.length < size - 1; i++) {
    const candidate = padding(i);
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    pool.push(candidate);
  }

  const wrongs = shuffle(rng, pool).slice(0, size - 1);
  const opciones = shuffle(rng, [correct, ...wrongs]);
  const correcta = opciones.indexOf(correct);
  if (misconceptionIndex === null) return { opciones, correcta, distractorIndex: null };
  const misconceptionLabel = pool[misconceptionIndex];
  const distractorIndex = opciones.indexOf(misconceptionLabel);
  return { opciones, correcta, distractorIndex: distractorIndex >= 0 ? distractorIndex : null };
}

/** Suma columna por columna sin acarrear: el error clásico al "olvidar llevar". */
function sumaSinLlevar(a: number, b: number): number {
  return (Math.floor(a / 10) + Math.floor(b / 10)) * 10 + ((a % 10) + (b % 10)) % 10;
}

/** Resta el dígito mayor menos el menor sin pedir prestado: error clásico. */
function restaSinPrestar(a: number, b: number): number {
  const tens = Math.floor(a / 10) - Math.floor(b / 10);
  return tens * 10 + Math.abs((a % 10) - (b % 10));
}

function transposed(value: number): number {
  const text = String(value);
  if (text.length < 2) return value + 1;
  const i = text.length - 2;
  if (text[i] === text[i + 1]) return value + 1;
  const swapped = text.slice(0, i) + text[i + 1] + text[i];
  return Number(swapped);
}

interface Fraction {
  n: number;
  d: number;
}

function fractionLabel(f: Fraction): string {
  return `${f.n}/${f.d}`;
}

function generateSum(rng: () => number, min: number, max: number): GeneratedItem {
  const a = intInRange(rng, min, max);
  const b = intInRange(rng, min, max);
  const answer = a + b;
  const label = formatNumber(answer);
  const carry = a % 10 + b % 10 >= 10;
  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    label,
    carry ? [formatNumber(sumaSinLlevar(a, b))] : [formatNumber(transposed(answer))],
    (i) => formatNumber(answer + (i % 2 === 0 ? 10 : -10) * (Math.floor(i / 2) + 1))
  );
  return {
    tipo: 'suma',
    pregunta: `¿Cuánto es ${formatNumber(a)} + ${formatNumber(b)}?`,
    expresion: `${formatNumber(a)} + ${formatNumber(b)}`,
    opciones,
    correcta,
    respuesta: answer,
    distractorIndex,
    misconception: carry
      ? `Al sumar las unidades (${a % 10} + ${b % 10} = ${a % 10 + b % 10}) se pasa de 9: hay que llevar 1 decena. ` +
        'Si lo olvidas, el resultado queda una decena corto.'
      : null,
    explicacion: `${formatNumber(a)} + ${formatNumber(b)} = ${label}.`
  };
}

function generateSub(rng: () => number, min: number, max: number): GeneratedItem {
  let a = intInRange(rng, min, max);
  let b = intInRange(rng, min, max);
  if (b > a) [a, b] = [b, a];
  const answer = a - b;
  const label = formatNumber(answer);
  const borrow = a % 10 < b % 10;
  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    label,
    borrow
      ? [formatNumber(restaSinPrestar(a, b))]
      : [formatNumber(transposed(answer)), formatNumber(answer + 10)],
    (i) => formatNumber(Math.max(0, answer + (i % 2 === 0 ? 1 : -1) * (Math.floor(i / 2) + 1)))
  );
  return {
    tipo: 'resta',
    pregunta: `¿Cuánto es ${formatNumber(a)} − ${formatNumber(b)}?`,
    expresion: `${formatNumber(a)} − ${formatNumber(b)}`,
    opciones,
    correcta,
    respuesta: answer,
    distractorIndex,
    misconception: borrow
      ? `Como ${a % 10} es menor que ${b % 10}, hay que pedir prestada una decena antes de restar las unidades. ` +
        'Restar siempre el mayor menos el menor da un resultado demasiado grande.'
      : null,
    explicacion: `${formatNumber(a)} − ${formatNumber(b)} = ${label}.`
  };
}

function generateMul(rng: () => number, min: number, max: number): GeneratedItem {
  const a = intInRange(rng, Math.max(2, min), Math.max(3, max));
  const b = intInRange(rng, Math.max(2, min), Math.max(3, max));
  const answer = a * b;
  const label = formatNumber(answer);
  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    label,
    [formatNumber((a + 1) * b), formatNumber(a * (b - 1)), formatNumber(a + b)],
    (i) => formatNumber(answer + (i + 1) * b)
  );
  return {
    tipo: 'multiplicacion',
    pregunta: `¿Cuánto es ${a} × ${b}?`,
    expresion: `${a} × ${b}`,
    opciones,
    correcta,
    respuesta: answer,
    distractorIndex,
    misconception: `${a} × ${b} significa ${b} sumado ${a} veces, no ${a} + ${b}. ` +
      `Cuenta de ${b} en ${b}: ${Array.from({ length: Math.min(a, 5) }, (_, i) => formatNumber(b * (i + 1))).join(', ')}${a > 5 ? ', …' : ''}.`,
    explicacion: `${a} × ${b} = ${label}.`
  };
}

function generateDiv(rng: () => number, min: number, max: number): GeneratedItem {
  const divisor = intInRange(rng, Math.max(2, min), Math.max(3, max));
  const quotient = intInRange(rng, Math.max(2, min), Math.max(9, max));
  const dividend = divisor * quotient;
  const label = formatNumber(quotient);
  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    label,
    [formatNumber(quotient + 1), formatNumber(Math.max(1, quotient - 1)), formatNumber(dividend)],
    (i) => formatNumber(quotient + i + 2)
  );
  return {
    tipo: 'division',
    pregunta: `¿Cuánto es ${formatNumber(dividend)} ÷ ${divisor}?`,
    expresion: `${formatNumber(dividend)} ÷ ${divisor}`,
    opciones,
    correcta,
    respuesta: quotient,
    distractorIndex,
    misconception: `Dividir es repartir, no multiplicar: ${formatNumber(dividend)} ÷ ${divisor} busca cuántas veces cabe ${divisor} en ${formatNumber(dividend)}.`,
    explicacion: `${divisor} × ${quotient} = ${formatNumber(dividend)}, así que ${formatNumber(dividend)} ÷ ${divisor} = ${label}.`
  };
}

function generatePlaceValue(rng: () => number, min: number, max: number): GeneratedItem {
  const lower = Math.max(10, min);
  const upper = Math.max(lower + 1, max);
  const value = intInRange(rng, lower, upper);
  const digits = String(value).split('');
  const positions = digits
    .map((digit, index) => ({ digit, index }))
    .filter(({ digit }) => digit !== '0')
    .filter(({ digit }) => digits.filter((d) => d === digit).length === 1);

  const chosen = positions.length > 0
    ? positions[intInRange(rng, 0, positions.length - 1)]
    : { digit: digits[digits.length - 1], index: digits.length - 1 };

  const place = 10 ** (digits.length - 1 - chosen.index);
  const digitValue = Number(chosen.digit) * place;
  const placeName = place === 1 ? 'unidades' : place === 10 ? 'decenas' : place === 100 ? 'centenas' : 'millares';
  const label = formatNumber(digitValue);
  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    label,
    [chosen.digit, formatNumber(digitValue / 10), formatNumber(digitValue * 10)],
    (i) => formatNumber(digitValue + (i + 1) * place)
  );
  return {
    tipo: 'valor-posicional',
    pregunta: `¿Cuánto vale el ${chosen.digit} en ${formatNumber(value)}?`,
    expresion: formatNumber(value),
    opciones,
    correcta,
    respuesta: digitValue,
    distractorIndex,
    misconception: `El ${chosen.digit} está en el lugar de las ${placeName}, así que vale ${chosen.digit} × ${formatNumber(place)} = ${label}, no ${chosen.digit}.`,
    explicacion: `En ${formatNumber(value)} el ${chosen.digit} ocupa las ${placeName}: vale ${label}.`
  };
}

const DEFAULT_DENOMINATORS = [2, 3, 4, 5, 6, 8];

function generateFraction(rng: () => number, denominators: number[] | undefined): GeneratedItem {
  const pool = denominators && denominators.length >= 2 ? denominators : DEFAULT_DENOMINATORS;
  const d = pool[intInRange(rng, 0, pool.length - 1)];
  const n = intInRange(rng, 1, d - 1);
  const k = intInRange(rng, 2, 4);
  const base: Fraction = { n, d };
  const correct: Fraction = { n: n * k, d: d * k };
  const correctLabel = fractionLabel(correct);

  const candidates = [
    { label: fractionLabel({ n: n + 1, d: d + 1 }), why: 'sumar lo mismo arriba y abajo' },
    { label: fractionLabel({ n: n * k, d }), why: 'multiplicar sólo el numerador' },
    { label: fractionLabel({ n, d: d * k }), why: 'multiplicar sólo el denominador' },
    { label: fractionLabel({ n: n + k, d: d + k }), why: `sumar ${k} arriba y abajo` }
  ];

  const seen = new Set<string>([correctLabel]);
  const distractors: string[] = [];
  let misconception: string | null = null;
  for (const candidate of candidates) {
    const parts = candidate.label.split('/').map(Number);
    const isEquivalentToBase = parts[0] * base.d === base.n * parts[1];
    if (seen.has(candidate.label) || candidate.label === correctLabel || isEquivalentToBase) continue;
    seen.add(candidate.label);
    distractors.push(candidate.label);
    if (misconception === null) {
      misconception =
        `Esa fracción sale de ${candidate.why}, y eso cambia la cantidad. ` +
        `Para que ${fractionLabel(base)} no varíe hay que multiplicar arriba y abajo por el mismo número: ` +
        `${n} × ${k} = ${correct.n} y ${d} × ${k} = ${correct.d}.`;
    }
  }

  const { opciones, correcta, distractorIndex } = buildOptions(
    rng,
    correctLabel,
    distractors,
    (i) => fractionLabel({ n: n * (k + i + 1), d: d * (k + i + 1) + 1 })
  );

  return {
    tipo: 'fraccion-equivalente',
    pregunta: `¿Qué fracción es equivalente a ${fractionLabel(base)}?`,
    expresion: fractionLabel(base),
    opciones,
    correcta,
    respuesta: correctLabel,
    distractorIndex,
    misconception,
    explicacion: `${fractionLabel(base)} = ${correctLabel} porque ${n} × ${k} = ${correct.n} y ${d} × ${k} = ${correct.d}.`
  };
}

/** Genera un ítem coherente con el generador pedido. */
export function generateItem(generator: ItemGenerator, rng: () => number): GeneratedItem {
  switch (generator.tipo) {
    case 'suma':
      return generateSum(rng, generator.min, generator.max);
    case 'resta':
      return generateSub(rng, generator.min, generator.max);
    case 'multiplicacion':
      return generateMul(rng, generator.min, generator.max);
    case 'division':
      return generateDiv(rng, generator.min, generator.max);
    case 'valor-posicional':
      return generatePlaceValue(rng, generator.min, generator.max);
    case 'fraccion-equivalente':
      return generateFraction(rng, generator.denominadores);
  }
}

/**
 * Genera `cantidad` ítems sin repetir la misma pregunta.
 * La semilla fija el resultado, así el alumno que reintenta ve los mismos números.
 */
export function generateItems(generator: ItemGenerator, cantidad: number, semilla: number): GeneratedItem[] {
  const rng = createRng(semilla);
  const items: GeneratedItem[] = [];
  const seen = new Set<string>();
  let guard = 0;
  while (items.length < Math.max(1, cantidad) && guard < cantidad * 25 + 50) {
    guard++;
    const item = generateItem(generator, rng);
    if (seen.has(item.pregunta)) continue;
    seen.add(item.pregunta);
    items.push(item);
  }
  if (items.length === 0) items.push(generateItem(generator, rng));
  return items;
}

/** Generadores cuya respuesta es numérica (aptos para teclado numérico). */
const NUMERIC_GENERATORS = new Set<ItemGenerator['tipo']>([
  'suma',
  'resta',
  'multiplicacion',
  'division',
  'valor-posicional'
]);

export function isNumericGenerator(generator: ItemGenerator): boolean {
  return NUMERIC_GENERATORS.has(generator.tipo);
}

export function checkChoice(item: GeneratedItem, index: number): boolean {
  return index === item.correcta;
}

export function checkNumeric(item: GeneratedItem, value: number): boolean {
  return typeof item.respuesta === 'number' && value === item.respuesta;
}

/** Retroalimentación concreta según la opción elegida. */
export function feedbackFor(item: GeneratedItem, index: number): string | null {
  if (index === item.correcta) return item.explicacion;
  if (index === item.distractorIndex) return item.misconception;
  return null;
}
